/**
 * Foundations · Quick Reset Ritual
 *
 * Feb 23 2026 · Michael. "The resets — we do not need a script. They
 * are the resets from the main Reclaim app (orange floating button).
 * Just copy this exactly and ensure they have the same look and feel
 * as the rest of the Foundations app."
 *
 * Behaviour: identical to `ResetRitual.jsx`. Two guided breath cycles
 * synced to the shipped `2_breaths.mp3`, then Michael's pre-recorded
 * gratitude line over warm ambient, then a soft "You are reset."
 *
 * Skin: Foundations palette only — deep navy #060e1f background, cream
 * text, champagne-gold BreathOrb + gold heart pulse. Serif headlines
 * (Playfair Display), Manrope body. Zero cyan / teal.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Sparkles, Volume2, Wind } from "lucide-react";
import AudioWaveform from "../../components/AudioWaveform";
import BreathOrb from "../../components/BreathOrb";
import { voiceVolume, musicVolume, getStoredAudioMode } from "../../utils/modeAudio";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Foundations tokens (must stay in step with FoundationsHome.jsx) ─
const TOK = {
  bg: "radial-gradient(ellipse at 50% 22%, #1a2e50 0%, #0d1f3a 50%, #060e1f 85%)",
  cream: "#f5ecd9",
  creamStrong: "rgba(245,236,217,0.94)",
  creamDim: "rgba(245,236,217,0.82)",
  creamMist: "rgba(245,236,217,0.55)",
  gold: "#e0a97a",
  goldWarm: "#f0c790",
  goldSoft: "rgba(224,169,122,0.9)",
  goldMist: "rgba(224,169,122,0.24)",
  goldGlow: "rgba(224,169,122,0.32)",
};
const SERIF = '"Playfair Display", "Cormorant Garamond", serif';
const SANS = '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif';

// Same shipped assets as Reclaim ResetRitual — no duplication.
const TWO_BREATHS_AUDIO = `${API_URL}/api/breathing-audio/2_breaths.mp3`;
const GRATITUDE_AUDIO = `${API_URL}/api/daily-ritual/gratitude-audio-file`;

const getAmbientMusicOptions = () => {
  const cb = `?cb=${Date.now()}`;
  // Feb 27 2026 · Michael: "Choose a beautiful soothing track that
  // we have not heard regularly." Swapped out the trio of
  // nature/healing/meditation (used by every ritual in the app)
  // for the quieter, gentler `riverglass_harmony` — soft piano
  // over gentle river bed — with `safe_linen_air` and
  // `pillow_static` as unheard fall-backs.
  return [
    `${API_URL}/api/audio/ambient/riverglass_harmony.mp3${cb}`,
    `${API_URL}/api/audio/ambient/safe_linen_air.mp3${cb}`,
    `${API_URL}/api/audio/ambient/pillow_static.mp3${cb}`,
  ];
};

// 4 phases per cycle · 2 cycles · ~7.19 s per phase (matches audio).
const BREATH_PHASE_DURATION = 7187;
const BREATH_PHASES = ["inhale", "hold", "release", "rest"];
const TOTAL_BREATH_CYCLES = 2;
const BREATH_PHASE_TEXT = {
  inhale: "Breathe in…",
  hold: "Hold…",
  release: "Release…",
  rest: "Breathe normally…",
};

// Matches Michael's recorded gratitude line.
const RESET_GRATITUDE_TEXT = [
  "Take a moment to reconnect.",
  "Drop into that deep feeling of gratitude.",
  "Feel it on every level.",
];

const FoundationsResetRitual = ({ isOpen, onClose }) => {
  const [isReady, setIsReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [phase, setPhase] = useState("breathing");
  const [breathPhase, setBreathPhase] = useState("ready");
  const [breathCount, setBreathCount] = useState(0);
  const [gratitudeLineIndex, setGratitudeLineIndex] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [guidedVoiceEnabled, setGuidedVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem("reset_guided_voice");
    return saved !== null ? saved === "true" : true;
  });

  const breathingAudioRef = useRef(null);
  const gratitudeAudioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const breathTimerRef = useRef(null);
  const gratitudeTimersRef = useRef([]);
  const isCleanedUp = useRef(false);

  const toggleGuidedVoice = () => {
    const next = !guidedVoiceEnabled;
    setGuidedVoiceEnabled(next);
    localStorage.setItem("reset_guided_voice", String(next));
  };

  const cleanup = useCallback(() => {
    isCleanedUp.current = true;
    if (breathTimerRef.current) {
      clearTimeout(breathTimerRef.current);
      breathTimerRef.current = null;
    }
    if (gratitudeTimersRef.current.length > 0) {
      gratitudeTimersRef.current.forEach((t) => clearTimeout(t));
      gratitudeTimersRef.current = [];
    }
    if (breathingAudioRef.current) {
      breathingAudioRef.current.pause();
      breathingAudioRef.current = null;
    }
    if (gratitudeAudioRef.current) {
      gratitudeAudioRef.current.pause();
      gratitudeAudioRef.current = null;
    }
    if (ambientAudioRef.current) {
      ambientAudioRef.current.pause();
      ambientAudioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      isCleanedUp.current = false;
      setIsReady(true);
      setHasStarted(false);
      setPhase("breathing");
      setBreathPhase("ready");
      setBreathCount(0);
      setGratitudeLineIndex(0);
      setAudioPlaying(false);
    }
    return cleanup;
  }, [isOpen, cleanup]);

  const playAmbientMusicWithFallback = async () => {
    const options = getAmbientMusicOptions();
    for (let i = 0; i < options.length; i++) {
      try {
        const audio = new Audio(options[i]);
        audio.volume = 0.35;
        audio.loop = true;
        await audio.play();
        ambientAudioRef.current = audio;
        return;
      } catch (_) { /* try next */ }
    }
  };

  const runBreathingCycle = (cycle, phaseIndex) => {
    if (isCleanedUp.current) return;
    if (cycle >= TOTAL_BREATH_CYCLES) {
      startGratitudePhase();
      return;
    }
    setBreathPhase(BREATH_PHASES[phaseIndex]);
    breathTimerRef.current = setTimeout(() => {
      const nextPhase = (phaseIndex + 1) % 4;
      const nextCycle = nextPhase === 0 ? cycle + 1 : cycle;
      if (nextPhase === 0) setBreathCount(nextCycle);
      runBreathingCycle(nextCycle, nextPhase);
    }, BREATH_PHASE_DURATION);
  };

  const startBreathing = async () => {
    if (isCleanedUp.current) return;
    setBreathPhase("inhale");
    setBreathCount(0);
    await playAmbientMusicWithFallback();
    if (guidedVoiceEnabled) {
      try {
        breathingAudioRef.current = new Audio(TWO_BREATHS_AUDIO);
        breathingAudioRef.current.volume = voiceVolume(getStoredAudioMode());
        await breathingAudioRef.current.play();
      } catch (_) { /* silent */ }
    }
    runBreathingCycle(0, 0);
  };

  const handleStart = async () => {
    setHasStarted(true);
    await startBreathing();
  };

  const runGratitudeTextAdvancement = (durationMs) => {
    gratitudeTimersRef.current.forEach((t) => clearTimeout(t));
    gratitudeTimersRef.current = [];
    const lineCount = RESET_GRATITUDE_TEXT.length;
    const lineDelay = durationMs / lineCount;
    for (let i = 1; i < lineCount; i++) {
      const t = setTimeout(() => {
        if (!isCleanedUp.current) setGratitudeLineIndex(i);
      }, lineDelay * i);
      gratitudeTimersRef.current.push(t);
    }
    if (!guidedVoiceEnabled) {
      const done = setTimeout(() => {
        if (!isCleanedUp.current) completeRitual();
      }, durationMs + 1500);
      gratitudeTimersRef.current.push(done);
    }
  };

  const playGratitudeAudio = () => {
    if (isCleanedUp.current) return;
    const url = `${GRATITUDE_AUDIO}?cb=${Date.now()}`;
    const audio = new Audio(url);
    audio.volume = voiceVolume(getStoredAudioMode());
    audio.preload = "auto";
    gratitudeAudioRef.current = audio;
    runGratitudeTextAdvancement(12000);
    audio.onplay = () => { if (!isCleanedUp.current) setAudioPlaying(true); };
    audio.onended = () => {
      if (isCleanedUp.current) return;
      setAudioPlaying(false);
      const done = setTimeout(() => {
        if (!isCleanedUp.current) completeRitual();
      }, 1500);
      gratitudeTimersRef.current.push(done);
    };
    audio.play().catch(() => { /* silent */ });
  };

  const startGratitudePhase = () => {
    if (isCleanedUp.current) return;
    if (breathingAudioRef.current) {
      breathingAudioRef.current.pause();
      breathingAudioRef.current = null;
    }
    setPhase("gratitude");
    setGratitudeLineIndex(0);
    if (guidedVoiceEnabled) playGratitudeAudio();
    else runGratitudeTextAdvancement(12000);
  };

  const completeRitual = () => {
    if (ambientAudioRef.current) {
      const fade = setInterval(() => {
        const a = ambientAudioRef.current;
        if (a && a.volume > 0.05) a.volume -= 0.05;
        else {
          clearInterval(fade);
          if (ambientAudioRef.current) {
            ambientAudioRef.current.pause();
            ambientAudioRef.current = null;
          }
        }
      }, 100);
    }
    setPhase("complete");
  };

  const handleClose = () => {
    cleanup();
    onClose();
  };

  if (!isOpen) return null;

  // ── Intro screen ─────────────────────────────────────────────
  if (isReady && !hasStarted) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center px-8"
          style={{ background: TOK.bg, color: TOK.cream }}
          data-testid="foundations-reset-intro"
        >
          <div className="absolute top-6 right-6">
            <button
              onClick={handleClose}
              data-testid="foundations-reset-close"
              style={{ color: TOK.creamMist, background: "transparent" }}
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="relative mb-8 flex items-center justify-center">
            <BreathOrb size={120} tone="gold" pulseMs={4000} />
          </div>

          <div
            className="uppercase mb-3"
            style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.4em" }}
          >
            Foundations · Reset
          </div>

          <h2
            className="italic mb-4 text-center"
            style={{ fontFamily: SERIF, color: TOK.cream, fontSize: "clamp(1.6rem, 6vw, 2rem)", fontWeight: 300 }}
          >
            A quiet moment.
          </h2>
          <p
            className="text-center max-w-sm"
            style={{ color: TOK.creamStrong, fontFamily: SANS, fontSize: "1rem", lineHeight: 1.6 }}
          >
            Ninety seconds. Two slow breaths, and a small moment of gratitude.
          </p>

          {/* Guided voice toggle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8 mb-8"
          >
            <button
              onClick={toggleGuidedVoice}
              className="flex items-center gap-3 rounded-full px-4 py-2 transition-colors"
              style={{
                background: guidedVoiceEnabled ? "rgba(224,169,122,0.14)" : "rgba(245,236,217,0.05)",
                border: `1px solid ${guidedVoiceEnabled ? TOK.goldMist : "rgba(245,236,217,0.14)"}`,
                color: guidedVoiceEnabled ? TOK.goldWarm : TOK.creamMist,
              }}
              data-testid="foundations-reset-voice-toggle"
            >
              <Volume2 size={16} />
              <span style={{ fontFamily: SANS, fontSize: "0.78rem" }}>Guided voice</span>
              <div
                className="relative"
                style={{
                  width: 36, height: 18, borderRadius: 999,
                  background: guidedVoiceEnabled ? TOK.gold : "rgba(245,236,217,0.2)",
                }}
              >
                <motion.div
                  animate={{ x: guidedVoiceEnabled ? 20 : 2 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  style={{
                    position: "absolute", top: 2, width: 14, height: 14, borderRadius: 999,
                    background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                  }}
                />
              </div>
            </button>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={handleStart}
            className="rounded-full flex items-center gap-3"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5",
              color: "#1a0f05",
              padding: "16px 30px",
              fontFamily: SANS,
              fontSize: "0.82rem",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              boxShadow: `0 12px 44px ${TOK.goldGlow}`,
              minHeight: 56,
            }}
            data-testid="foundations-reset-start"
          >
            <Wind size={18} />
            <span>Begin Reset</span>
          </motion.button>

          <button
            onClick={handleClose}
            className="mt-6 uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.58rem", letterSpacing: "0.28em", padding: "10px 6px",
            }}
            data-testid="foundations-reset-later"
          >
            Maybe later
          </button>
        </motion.div>
      </AnimatePresence>
    );
  }

  // ── Ritual screen (breathing → gratitude → complete) ─────────
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col"
        style={{ background: TOK.bg, color: TOK.cream }}
        data-testid="foundations-reset-modal"
      >
        <div className="p-6 flex justify-end">
          <button
            onClick={handleClose}
            data-testid="foundations-reset-modal-close"
            style={{ color: TOK.creamMist, background: "transparent" }}
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-8">
          {phase === "breathing" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <div className="text-center mb-8">
                <p
                  className="uppercase"
                  style={{
                    color: TOK.goldSoft, fontFamily: SANS,
                    fontSize: "0.62rem", letterSpacing: "0.28em",
                  }}
                >
                  Breath {breathCount + 1} of {TOTAL_BREATH_CYCLES}
                </p>
              </div>

              <div className="relative mb-12 flex items-center justify-center" style={{ width: 224, height: 224 }}>
                <BreathOrb size={224} phase={breathPhase} tone="gold" data-testid="foundations-reset-orb" />
              </div>

              <AnimatePresence mode="wait">
                <motion.p
                  key={breathPhase}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="italic"
                  style={{ color: TOK.creamStrong, fontFamily: SERIF, fontSize: "1.5rem", fontWeight: 300 }}
                >
                  {BREATH_PHASE_TEXT[breathPhase] || "…"}
                </motion.p>
              </AnimatePresence>
            </motion.div>
          )}

          {phase === "gratitude" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center text-center"
            >
              {/* Gold heart pulse — replaces Reclaim's cyan pulse. */}
              <div className="relative mb-12">
                <motion.div
                  animate={{ scale: [1, 1.15, 1], opacity: [0.55, 0.85, 0.55] }}
                  transition={{ duration: 3, ease: "easeInOut", repeat: Infinity }}
                  className="rounded-full flex items-center justify-center"
                  style={{
                    width: 128, height: 128,
                    background: "radial-gradient(circle, rgba(240,199,144,0.55) 0%, rgba(224,169,122,0.30) 60%, rgba(224,169,122,0) 100%)",
                    border: `1px solid ${TOK.goldMist}`,
                  }}
                >
                  <Heart size={38} style={{ color: TOK.goldWarm }} strokeWidth={1.5} />
                </motion.div>
                <motion.div
                  animate={{ scale: [1.2, 1.5, 1.2], opacity: [0.2, 0.42, 0.2] }}
                  transition={{ duration: 3, ease: "easeInOut", repeat: Infinity }}
                  className="absolute inset-0 rounded-full -z-10"
                  style={{
                    width: 128, height: 128,
                    background: TOK.goldGlow,
                    filter: "blur(28px)",
                  }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.p
                  key={gratitudeLineIndex}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.5 }}
                  className="italic leading-relaxed max-w-sm"
                  style={{ color: TOK.creamStrong, fontFamily: SERIF, fontSize: "1.35rem", fontWeight: 300 }}
                >
                  {RESET_GRATITUDE_TEXT[gratitudeLineIndex]}
                </motion.p>
              </AnimatePresence>

              {audioPlaying && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-8 flex items-center gap-3"
                  style={{ color: TOK.goldSoft }}
                >
                  <AudioWaveform isPlaying={true} barCount={5} color={TOK.gold} size="sm" />
                  <Volume2 size={14} />
                  <span className="uppercase" style={{ fontFamily: SANS, fontSize: "0.56rem", letterSpacing: "0.28em" }}>
                    Your voice
                  </span>
                </motion.div>
              )}
            </motion.div>
          )}

          {phase === "complete" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center text-center"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="relative mb-12"
              >
                <div
                  className="rounded-full flex items-center justify-center"
                  style={{
                    width: 128, height: 128,
                    background: "radial-gradient(circle, rgba(240,199,144,0.55) 0%, rgba(224,169,122,0.28) 60%, rgba(224,169,122,0) 100%)",
                    border: `1px solid ${TOK.goldMist}`,
                  }}
                >
                  <Sparkles size={38} style={{ color: TOK.goldWarm }} strokeWidth={1.5} />
                </div>
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.28, 0.5, 0.28] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full -z-10"
                  style={{ width: 128, height: 128, background: TOK.goldGlow, filter: "blur(28px)" }}
                />
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="italic max-w-sm"
                style={{ color: TOK.cream, fontFamily: SERIF, fontSize: "1.6rem", fontWeight: 300 }}
              >
                You are reset.
              </motion.p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-3"
                style={{ color: TOK.creamMist, fontFamily: SANS, fontSize: "0.95rem" }}
              >
                Carry this feeling forward.
              </motion.p>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                onClick={handleClose}
                className="mt-10 rounded-full uppercase"
                style={{
                  background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                  border: "1px solid #f5e0c5",
                  color: "#1a0f05",
                  padding: "14px 30px",
                  fontFamily: SANS,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.22em",
                  minHeight: 52,
                  boxShadow: `0 12px 44px ${TOK.goldGlow}`,
                }}
                data-testid="foundations-reset-done"
              >
                Done
              </motion.button>
            </motion.div>
          )}
        </div>

        <div className="p-6 flex justify-center gap-2">
          {["breathing", "gratitude", "complete"].map((p, i) => {
            const currentIdx = ["breathing", "gratitude", "complete"].indexOf(phase);
            const isActive = phase === p;
            const isPast = currentIdx > i;
            return (
              <div
                key={p}
                className="rounded-full transition-all duration-300"
                style={{
                  height: 6,
                  width: isActive ? 24 : 6,
                  background: isActive
                    ? TOK.gold
                    : isPast
                    ? TOK.goldSoft
                    : "rgba(245,236,217,0.18)",
                }}
              />
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FoundationsResetRitual;
